import type { PropsWithChildren } from 'react'
import { createContext, useCallback, useEffect, useMemo, useRef, useState } from 'react'

interface RateImpressionContextValue {
  hasFired: (key: string) => boolean
  markAsFired: (key: string) => void
  isReady: boolean
}

const RateImpressionContext = createContext<RateImpressionContextValue>({
  hasFired: () => false,
  markAsFired: () => {},
  isReady: true,
})

interface Props {
  availabilityKey?: string
  ratesLoading?: boolean
}

export function RateImpressionProvider(props: PropsWithChildren<Props>) {
  const { availabilityKey, ratesLoading = false, children } = props
  const firedImpressions = useRef<Set<string>>(new Set())
  const [trackedAvailabilityKey, setTrackedAvailabilityKey] = useState(availabilityKey)

  useEffect(() => {
    if (ratesLoading || availabilityKey === trackedAvailabilityKey) return

    // A date or occupancy change first commits the new offer-page state, then starts the
    // availability request from a sibling effect. Deferring this reset one task gives that
    // request a chance to mark the new key as loading, so a retained tile cannot emit its old
    // price against the newly selected stay.
    const timeoutId = setTimeout(() => {
      firedImpressions.current = new Set()
      setTrackedAvailabilityKey(availabilityKey)
    }, 0)

    return () => clearTimeout(timeoutId)
  }, [availabilityKey, ratesLoading, trackedAvailabilityKey])

  const hasFired = useCallback((key: string) => {
    return firedImpressions.current.has(key)
  }, [])

  const markAsFired = useCallback((key: string) => {
    firedImpressions.current.add(key)
  }, [])

  const isReady = availabilityKey === trackedAvailabilityKey

  const value = useMemo<RateImpressionContextValue>(() => ({
    hasFired,
    markAsFired,
    isReady,
  }), [hasFired, isReady, markAsFired])

  return <RateImpressionContext.Provider value={value}>
    {children}
  </RateImpressionContext.Provider>
}

export default RateImpressionContext
